import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import _get from 'lodash/get';
import {
  FormChecklistTemplate,
  FormChecklistTemplateCreateInputObject,
  FormChecklistTemplateOption,
  FormChecklistTemplateUpdateInputObject,
  useFormChecklistTemplateCreateMutation,
  useFormChecklistTemplateUpdateMutation,
} from 'graphql-common';
import useFormChecklistTemplatesQueryHook from 'utils/fetch/hooks/useFormChecklistTemplatesQueryHook';
import useFormChecklistTemplateQueryHook from 'utils/fetch/hooks/useFormChecklistTemplateQueryHook';
import { Values } from '@lib/interfaces/form';
import getPreparedDataFromValues from 'routes/FormChecklists/utils/getPreparedDataFromValues';
import { showGraphQLErrors, getGraphQLErrors } from '@lib/utils/graphQLErrors';
import toast from '@lib/components/Toast/Toast';
import { APP_URLS } from 'constants/urls';

export interface UseActionsResult {
  listQuery: ReturnType<typeof useFormChecklistTemplatesQueryHook>;
  onAdd: () => void;
  onCloseDestroyModal: () => void;
  onConfirmedDelete: () => void;
  onDestroy: (item?: FormChecklistTemplate) => void;
  onEdit: (item?: FormChecklistTemplate) => void;
  onCancel: () => void;
}

export default function useFormChecklistTemplatesActions({
  isEdit,
}: {
  isEdit?: boolean;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id = '' } = useParams();

  // Queries
  const {
    data,
    loading: dataLoading,
    firstLoading,
  } = useFormChecklistTemplateQueryHook({
    id,
  });

  // Mutations
  const [createMutation, createMutationResult] =
    useFormChecklistTemplateCreateMutation();
  const [updateMutation, updateMutationResult] =
    useFormChecklistTemplateUpdateMutation();

  // Actions
  const onSubmitForm = (values: Values) => {
    const name = _get(values, 'name');
    const description = _get(values, 'description');
    const options = _get(
      values,
      'options',
      [],
    ) as FormChecklistTemplateOption[];
    if (isEdit) {
      updateMutation({
        variables: {
          id,
          attributes: getPreparedDataFromValues({
            values: {
              name,
              description,
              options: options.map((option) => {
                if (option?.id?.indexOf('new') === 0) {
                  return {
                    ...option,
                    id: undefined,
                  };
                }
                return { ...option };
              }),
            },
          }) as FormChecklistTemplateUpdateInputObject,
        },
        onCompleted: () => {
          toast({ content: t('update-checklist-success') });
          navigate(APP_URLS.dashboard.formChecklists.index.getPathWithQuery());
        },
        onError: (error) => showGraphQLErrors(error, t),
      });
    } else {
      createMutation({
        variables: {
          attributes: getPreparedDataFromValues({
            values: {
              name,
              description,
              options: options.map((option) => {
                if (option?.id?.indexOf('new') === 0) {
                  return {
                    ...option,
                    id: undefined,
                  };
                }
                return { ...option };
              }),
            },
          }) as FormChecklistTemplateCreateInputObject,
        },
        onCompleted: () => {
          toast({ content: t('create-checklist-success') });
          navigate(APP_URLS.dashboard.formChecklists.index.getPathWithQuery());
        },
        onError: (error) => showGraphQLErrors(error, t),
      });
    }
  };

  const onCancel = () => {
    navigate(APP_URLS.dashboard.formChecklists.index.getPathWithQuery());
  };

  const loading =
    dataLoading || createMutationResult.loading || updateMutationResult.loading;
  const graphQLErrors =
    getGraphQLErrors(createMutationResult?.error) ||
    getGraphQLErrors(updateMutationResult?.error);

  return {
    data,
    firstLoading,
    graphQLErrors,
    loading,
    onCancel,
    onSubmitForm,
  };
}
